import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  AdjustmentsHorizontalIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  LanguageIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  configurationApi,
  Configuration,
  ConfigurationInput,
  TextCorrectionRule,
  TextCorrectionMatchType,
} from '@/services/configurationApi';
import { PermissionGuard } from '@/components/Auth/PermissionGuard';

// Configuration "types" available today. Add an entry here (and a matching
// editor modal) whenever a new feature configuration type is introduced.
const CONFIGURATION_TYPES = [
  {
    value: 'translation-text-correction' as const,
    label: 'Translation Text Correction',
    description: 'Per-language find/replace rules applied to translated text after it comes back from the translation engine — for fixing specific words, numbers, abbreviations, or proper nouns the engine gets wrong.',
  },
];

const emptyRule = (): TextCorrectionRule => ({ find: '', replace: '', matchType: 'word', caseSensitive: false });

const ConfigurationPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeType, setActiveType] = useState<typeof CONFIGURATION_TYPES[number]['value']>('translation-text-correction');
  const [editingConfig, setEditingConfig] = useState<Configuration | null | 'new'>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['configurations', activeType],
    queryFn: () => configurationApi.getAll({ type: activeType }).then(res => res.data.data.configurations),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => configurationApi.delete(id),
    onSuccess: () => {
      toast.success('Configuration deleted');
      queryClient.invalidateQueries({ queryKey: ['configurations'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to delete configuration'),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (config: Configuration) => configurationApi.update(config._id, { isActive: !config.isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configurations'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to update configuration'),
  });

  const handleDelete = (config: Configuration) => {
    if (!window.confirm(`Delete configuration "${config.name}"? This cannot be undone.`)) return;
    deleteMutation.mutate(config._id);
  };

  const activeTypeMeta = CONFIGURATION_TYPES.find(t => t.value === activeType)!;

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="mb-6 flex items-center gap-3">
        <div className="rounded-xl bg-indigo-100 p-2.5 dark:bg-indigo-900/30">
          <AdjustmentsHorizontalIcon className="h-7 w-7 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Configuration</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Feature-specific configurations that customize how the application behaves.</p>
        </div>
      </div>

      {/* Type tabs — currently just one, structured to grow */}
      <div className="mb-4 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-6">
          {CONFIGURATION_TYPES.map(t => (
            <button
              key={t.value}
              onClick={() => setActiveType(t.value)}
              className={`py-3 px-1 border-b-2 text-sm font-medium ${
                activeType === t.value
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="mb-6 flex items-start justify-between gap-4">
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-2xl">{activeTypeMeta.description}</p>
        <PermissionGuard module="configurations" action="create">
          <button
            type="button"
            onClick={() => setEditingConfig('new')}
            className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            <PlusIcon className="h-4 w-4" />
            Add Configuration
          </button>
        </PermissionGuard>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600" />
        </div>
      ) : !data?.length ? (
        <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 py-16 text-center">
          <AdjustmentsHorizontalIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No configurations yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.map(config => (
            <div key={config._id} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900 dark:text-white truncate">{config.name}</h3>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      config.isActive
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {config.isActive ? 'Active' : 'Inactive'}
                    </span>
                    {config.language && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
                        <LanguageIcon className="h-3 w-3" />
                        {config.language}
                      </span>
                    )}
                    {!config.language && (
                      <span className="inline-flex items-center rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/20 dark:text-purple-400">
                        All languages
                      </span>
                    )}
                  </div>
                  {config.description && (
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{config.description}</p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400 dark:text-gray-500">
                    <span>{config.data.rules?.length || 0} rule{config.data.rules?.length === 1 ? '' : 's'}</span>
                    {config.data.convertNumbersToWords && <span>• Numbers → words</span>}
                    {config.data.separateAbbreviationsWithDots && <span>• Abbreviations dotted</span>}
                  </div>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  <PermissionGuard module="configurations" action="update">
                    <button
                      type="button"
                      onClick={() => toggleActiveMutation.mutate(config)}
                      disabled={toggleActiveMutation.isLoading}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                        config.isActive ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                      title={config.isActive ? 'Deactivate' : 'Activate'}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                        config.isActive ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingConfig(config)}
                      className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700"
                      title="Edit"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  </PermissionGuard>
                  <PermissionGuard module="configurations" action="delete">
                    <button
                      type="button"
                      onClick={() => handleDelete(config)}
                      className="rounded-lg p-2 text-red-500 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/20"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </PermissionGuard>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editingConfig && (
        <TranslationCorrectionModal
          config={editingConfig === 'new' ? null : editingConfig}
          onClose={() => setEditingConfig(null)}
        />
      )}
    </div>
  );
};

// ── Translation Text Correction editor ────────────────────────────────────────
const MATCH_TYPE_OPTIONS: { value: TextCorrectionMatchType; label: string; hint: string }[] = [
  { value: 'word', label: 'Whole word', hint: 'Matches the word on its own, not inside a larger word' },
  { value: 'substring', label: 'Anywhere', hint: 'Matches anywhere it appears, including inside other words' },
  { value: 'regex', label: 'Regex', hint: 'Treats "Find" as a regular expression' },
];

const TranslationCorrectionModal: React.FC<{
  config: Configuration | null;
  onClose: () => void;
}> = ({ config, onClose }) => {
  const queryClient = useQueryClient();
  const isEditing = !!config;

  const [name, setName] = useState(config?.name || '');
  const [description, setDescription] = useState(config?.description || '');
  const [language, setLanguage] = useState(config?.language || '');
  const [isActive, setIsActive] = useState(config?.isActive ?? true);
  const [rules, setRules] = useState<TextCorrectionRule[]>(
    config?.data.rules?.length ? config.data.rules.map(r => ({ ...r })) : [emptyRule()]
  );
  const [convertNumbersToWords, setConvertNumbersToWords] = useState(config?.data.convertNumbersToWords ?? false);
  const [separateAbbreviationsWithDots, setSeparateAbbreviationsWithDots] = useState(config?.data.separateAbbreviationsWithDots ?? false);

  const saveMutation = useMutation({
    mutationFn: (input: ConfigurationInput) =>
      isEditing ? configurationApi.update(config!._id, input) : configurationApi.create(input),
    onSuccess: () => {
      toast.success(`Configuration ${isEditing ? 'updated' : 'created'} successfully`);
      queryClient.invalidateQueries({ queryKey: ['configurations'] });
      onClose();
    },
    onError: (err: any) => toast.error(err.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'} configuration`),
  });

  const updateRule = (index: number, patch: Partial<TextCorrectionRule>) => {
    setRules(prev => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const removeRule = (index: number) => {
    setRules(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    const cleanedRules = rules
      .map(r => ({ ...r, find: r.find.trim() }))
      .filter(r => r.find.length > 0);
    if (cleanedRules.length === 0 && !convertNumbersToWords && !separateAbbreviationsWithDots) {
      toast.error('Add at least one rule, or enable one of the options below');
      return;
    }

    saveMutation.mutate({
      type: 'translation-text-correction',
      name: name.trim(),
      description: description.trim() || undefined,
      language: language.trim() || undefined,
      isActive,
      data: {
        rules: cleanedRules,
        convertNumbersToWords,
        separateAbbreviationsWithDots,
      },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-2xl dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isEditing ? 'Edit' : 'Add'} Translation Text Correction
          </h2>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Hindi number & abbreviation fixes"
                maxLength={200}
                required
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Language <span className="text-gray-400">(leave blank to apply to all languages)</span>
              </label>
              <input
                type="text"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                placeholder="e.g., hi, ta, fr"
                maxLength={10}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <p className="mt-1 text-xs text-gray-400">Matches the translation's target language code (e.g. "hi" also matches "hi-IN").</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              role="switch"
              aria-checked={isActive}
              onClick={() => setIsActive(!isActive)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isActive ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-600'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                isActive ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
          </div>

          <div className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Convert numbers to words</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">e.g. "2026" → "Two Thousand Twenty Six" (in this configuration's language)</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={convertNumbersToWords}
                onClick={() => setConvertNumbersToWords(!convertNumbersToWords)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                  convertNumbersToWords ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-600'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                  convertNumbersToWords ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            {convertNumbersToWords && !language.trim() && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                No language set — set the language above so numbers convert using the correct language's word forms.
              </p>
            )}

            <div className="flex items-start justify-between gap-4 border-t border-gray-100 pt-3 dark:border-gray-700">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Separate abbreviation letters with dots</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">e.g. "NASA" → "N.A.S.A." — applies to standalone ALL-CAPS words of 2+ letters</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={separateAbbreviationsWithDots}
                onClick={() => setSeparateAbbreviationsWithDots(!separateAbbreviationsWithDots)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                  separateAbbreviationsWithDots ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-600'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                  separateAbbreviationsWithDots ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Rules</label>
              <button
                type="button"
                onClick={() => setRules(prev => [...prev, emptyRule()])}
                className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
              >
                <PlusIcon className="h-4 w-4" />
                Add rule
              </button>
            </div>

            <div className="space-y-3">
              {rules.map((rule, index) => (
                <div key={index} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Find</label>
                      <input
                        type="text"
                        value={rule.find}
                        onChange={(e) => updateRule(index, { find: e.target.value })}
                        placeholder="e.g., Rs."
                        className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Replace with</label>
                      <input
                        type="text"
                        value={rule.replace}
                        onChange={(e) => updateRule(index, { replace: e.target.value })}
                        placeholder="e.g., रुपये"
                        className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Match type</label>
                      <select
                        value={rule.matchType}
                        onChange={(e) => updateRule(index, { matchType: e.target.value as TextCorrectionMatchType })}
                        className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      >
                        {MATCH_TYPE_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 mt-4">
                      <input
                        type="checkbox"
                        checked={rule.caseSensitive}
                        onChange={(e) => updateRule(index, { caseSensitive: e.target.checked })}
                        className="rounded border-gray-300"
                      />
                      Case sensitive
                    </label>
                    <button
                      type="button"
                      onClick={() => removeRule(index)}
                      disabled={rules.length === 1}
                      className="ml-auto mt-4 inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-800 disabled:opacity-30 disabled:cursor-not-allowed dark:text-red-400"
                    >
                      <TrashIcon className="h-4 w-4" />
                      Remove
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-gray-400">
                    {MATCH_TYPE_OPTIONS.find(o => o.value === rule.matchType)?.hint}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saveMutation.isLoading}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saveMutation.isLoading ? 'Saving…' : isEditing ? 'Save Changes' : 'Create Configuration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConfigurationPage;
